import React, { useEffect, useMemo, useState } from "react";

export default function PatientFilterCard({
  isOpen,
  onClose,
  onApplyFilter,
  currentFilters = {},
  conditionMap = {},
}) {
  const [preset, setPreset] = useState("this_year_to_date");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [dateField, setDateField] = useState("last_visit");
  const [condition, setCondition] = useState("");
  const [reviewPending, setReviewPending] = useState("");

  const yyyyMmDd = (d) =>
    new Date(d.getTime() - d.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 10);

  const computePresetRange = (value) => {
    const now = new Date();
    if (value === "this_year_to_date") {
      const start = new Date(now.getFullYear(), 0, 1);
      return { from: yyyyMmDd(start), to: yyyyMmDd(now) };
    }
    if (value === "last_year") {
      const start = new Date(now.getFullYear() - 1, 0, 1);
      const end = new Date(now.getFullYear() - 1, 11, 31);
      return { from: yyyyMmDd(start), to: yyyyMmDd(end) };
    }
    if (value === "last_month") {
      const firstDayThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endLastMonth = new Date(firstDayThisMonth - 1);
      const startLastMonth = new Date(
        endLastMonth.getFullYear(),
        endLastMonth.getMonth(),
        1
      );
      return { from: yyyyMmDd(startLastMonth), to: yyyyMmDd(endLastMonth) };
    }
    // custom
    return { from: dateFrom || "", to: dateTo || "" };
  };

  useEffect(() => {
    if (preset !== "custom") {
      const { from, to } = computePresetRange(preset);
      setDateFrom(from);
      setDateTo(to);
    }
  }, [preset]);

  useEffect(() => {
    // seed from current filters if provided
    if (currentFilters) {
      if (currentFilters.dateFrom) setDateFrom(currentFilters.dateFrom);
      if (currentFilters.dateTo) setDateTo(currentFilters.dateTo);
      if (currentFilters.dateField) setDateField(currentFilters.dateField);
      if (
        currentFilters.condition !== undefined &&
        currentFilters.condition !== null
      ) {
        setCondition(String(currentFilters.condition));
      }
      if (
        currentFilters.reviewPending !== undefined &&
        currentFilters.reviewPending !== null
      ) {
        setReviewPending(String(currentFilters.reviewPending));
      }
    }
  }, [currentFilters]);

  const handleRunReport = () => {
    const payload = {
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      dateField: dateField || "last_visit",
      condition: condition ? Number(condition) : undefined,
      reviewPending: reviewPending ? reviewPending === "true" : undefined,
    };
    onApplyFilter(payload);
  };

  const handleClearFilters = () => {
    setPreset("custom");
    setDateFrom("");
    setDateTo("");
    setDateField("last_visit");
    setCondition("");
    setReviewPending("");
    onApplyFilter({});
  };

  const isCustom = preset === "custom";

  if (!isOpen) return null;

  return (
    <div className="filter-card">
      <div className="filter-card-header">
        <div className="filter-card-title">Filters</div>
        <button
          className="filter-card-close"
          onClick={onClose}
          aria-label="Close filters"
          type="button"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M18 6L6 18M6 6l12 12"
              stroke="#111827"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      <div className="filter-row">
        <div className="filter-group">
          <div className="filter-label">Date Field</div>
          <select
            className="filter-input"
            value={dateField}
            onChange={(e) => setDateField(e.target.value)}
          >
            <option value="dob">Date of Birth</option>
            <option value="last_visit">Last Visit</option>
            <option value="first_visit">First Visit</option>
          </select>
        </div>
        <div className="filter-group">
          <div className="filter-label">Date Range</div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <select
              className="filter-input"
              value={preset}
              onChange={(e) => setPreset(e.target.value)}
            >
              <option value="this_year_to_date">This Year-to-date</option>
              <option value="last_year">Last Year</option>
              <option value="last_month">Last Month</option>
              <option value="custom">Custom</option>
            </select>
            <input
              type="date"
              className="filter-input"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              disabled={!isCustom}
            />
            <input
              type="date"
              className="filter-input"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              disabled={!isCustom}
            />
          </div>
        </div>
      </div>

      <div className="filter-row">
        <div className="filter-group">
          <div className="filter-label">Condition</div>
          <select
            className="filter-input"
            value={condition}
            onChange={(e) => setCondition(e.target.value)}
          >
            <option value="">All</option>
            {Object.entries(conditionMap).map(([value, label]) => (
              <option value={value} key={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <div className="filter-label">Review Pending</div>
          <select
            className="filter-input"
            value={reviewPending}
            onChange={(e) => setReviewPending(e.target.value)}
          >
            <option value="">All</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </div>
        <div className="filter-actions">
          <button
            className="clear-filter-btn"
            type="button"
            onClick={handleClearFilters}
          >
            Clear filters
          </button>
          <button
            className="run-report-btn"
            type="button"
            onClick={handleRunReport}
          >
            Run report
          </button>
        </div>
      </div>
    </div>
  );
}
